import React, { useState, useRef, useEffect } from "react";
import { Modal, Spinner, Alert, ProgressBar } from "react-bootstrap";
import { FaCheckCircle, FaTimes } from "react-icons/fa";
import { useSearchParams } from "react-router-dom";
import { handleTokenAndPostRequest } from "../utils/utils";
import { useAgentDetails } from "../utils/useAgentDetails";
import AsyncSelect from "react-select/async";
import axios from "axios";
import { debounce } from "lodash";

function useIsMobile(breakpoint = 600) {
  const [isMobile, setIsMobile] = useState(window.innerWidth <= breakpoint);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth <= breakpoint);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [breakpoint]);
  return isMobile;
}

function ArticleEmailModal({ show, onHide, articleSlug }) {
  const [email, setEmail] = useState("");
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [error, setError] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const emailRef = useRef(null);

  const [searchParams] = useSearchParams();
  const agent = searchParams.get("agent");
  const { agentDetails, branding } = useAgentDetails();

  const isMobile = useIsMobile();

  // Hide modal if user scrolls above half a screen height (desktop only)
  useEffect(() => {
    if (!show || isMobile) return;
    const handleScroll = () => {
      const halfScreen = (window.innerHeight || document.documentElement.clientHeight) / 2;
      if ((window.scrollY || window.pageYOffset) < halfScreen) {
        onHide();
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [show, onHide, isMobile]);

  // Autofocus fix for mobile overlay
  useEffect(() => {
    if (show && isMobile && emailRef.current) {
      setTimeout(() => {
        try {
          emailRef.current.focus();
        } catch (e) {}
      }, 300);
    }
    if (!show) {
      setEmail("");
      setSelectedProperty(null);
      setError("");
      setSubmitted(false);
    }
  }, [show, isMobile]);

  // Desktop: autofocus as usual
  useEffect(() => {
    if (show && !isMobile && emailRef.current) {
      emailRef.current.focus();
    }
  }, [show, isMobile]);

  const validateEmail = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const loadProperties = debounce((inputValue, callback) => {
    axios.get(`${window.api_host}/property-search?q=${inputValue}`)
      .then(response => {
        const options = response.data.map(property => ({
          label: property.address_text,
          value: property.id,
          slug: property.slug
        }));
        callback(options);
      })
      .catch(error => {
        console.error('Error loading property options:', error);
        callback([]);
      });
  }, 500);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!validateEmail(email)) {
      setError("Please enter a valid email address.");
      return;
    }
    if (!selectedProperty) {
      setError("Please search and select a property address.");
      return;
    }

    // Show success and close immediately — fire-and-forget the API call
    setSubmitted(true);
    setTimeout(() => {
      onHide();
    }, 2500);

    // Fire the API request in the background (don't await)
    handleTokenAndPostRequest(
      `${window.api_host}/send-property-report`,
      {
        agent_slug: agent,
        email,
        property_slug: selectedProperty.slug,
        property_id: selectedProperty.value,
        address_text: selectedProperty.label,
        agency_id: agentDetails?.agency_id,
        agent_id: agentDetails?.agent_id,
        report_type: 'property_report',
      }
    ).catch(() => {});
  };

  const { accentColor, buttonTextColor, isLight, iconColor } = branding || {};

  // Consistent icon size and alignment
  const iconProps = { color: iconColor, size: 18, style: { marginRight: 10, minWidth: 18, marginTop: 3 } };

  const selectStyles = {
    control: (provided, state) => ({
      ...provided,
      borderRadius: 12,
      textAlign: "left",
      fontSize: '0.88rem',
      minHeight: 48,
      height: 48,
      border: '1px solid #e5e7eb',
      boxShadow: state.isFocused ? `0 0 0 1px ${accentColor}` : 'none',
      '&:hover': { borderColor: '#d1d5db' },
    }),
    placeholder: (provided) => ({
      ...provided,
      textAlign: "left",
      color: "#9ca3af",
    }),
    menu: (provided) => ({
      ...provided,
      textAlign: "left",
      zIndex: 100000,
      borderRadius: 12,
      boxShadow: '0 8px 24px rgba(0,0,0,0.12)',
      ...(isMobile ? { maxHeight: 180 } : {}),
    }),
    menuList: (provided) => ({
      ...provided,
      ...(isMobile ? { maxHeight: 180 } : {}),
    }),
    menuPortal: (provided) => ({
      ...provided,
      zIndex: 100000,
    }),
    singleValue: (provided) => ({
      ...provided,
      textAlign: "left",
    }),
    valueContainer: (provided) => ({
      ...provided,
      padding: "0 14px",
    }),
    option: (provided, state) => ({
      ...provided,
      fontSize: '0.88rem',
      backgroundColor: state.isFocused ? '#f3f4f6' : '#fff',
      color: '#111',
      cursor: 'pointer',
    }),
  };

  const closeButtonStyle = {
    position: "absolute",
    top: 0,
    right: 0,
    background: "#f3f4f6",
    border: "none",
    cursor: "pointer",
    padding: 0,
    lineHeight: 1,
    zIndex: 10,
    color: "#6b7280",
    borderRadius: "50%",
    width: 32,
    height: 32,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: 'background 0.15s',
  };

  // Success state content
  const successContent = (
    <div style={{ textAlign: "center", padding: "2rem 0", width: "100%" }}>
      <FaCheckCircle size={44} color={iconColor} style={{ marginBottom: 16 }} />
      <h3 style={{ fontWeight: 700, fontSize: "1.2rem", marginBottom: 8, color: '#111' }}>
        Your report is on its way!
      </h3>
      <p style={{ color: "#6b7280", fontSize: "0.88rem", marginBottom: 20 }}>
        We're generating your personalised property report now. It will arrive in your inbox shortly.
      </p>
      <div style={{ width: "100%", height: 4, backgroundColor: "#f3f4f6", borderRadius: 2, overflow: "hidden" }}>
        <div
          style={{
            height: "100%",
            backgroundColor: accentColor,
            borderRadius: 2,
            animation: "progressFill 2.5s linear forwards",
          }}
        />
      </div>
      <style>{`
        @keyframes progressFill {
          from { width: 0%; }
          to { width: 100%; }
        }
      `}</style>
    </div>
  );

  // Form content JSX (shared by both mobile and desktop)
  const content = (
    <div
      style={{
        width: "100%",
        maxWidth: 440,
        position: "relative",
      }}
    >
      {!submitted && (
        <button onClick={onHide} style={closeButtonStyle} aria-label="Close">
          <FaTimes size={14} />
        </button>
      )}
      {submitted ? successContent : (
        <>
          <div
            className="modal-title-custom"
            style={{
              fontWeight: 700,
              fontSize: isMobile ? "1.55rem" : "1.2rem",
              paddingBottom: isMobile ? "0.6rem" : "0.5rem",
              textAlign: "left",
              paddingRight: 30,
              color: '#111',
            }}
          >
            What's your home really worth?
          </div>
          <p className="modal-desc-custom" style={{ fontWeight: 400, marginBottom: 20, color: "#6b7280", fontSize: '0.88rem', lineHeight: 1.5 }}>
            Get a free AI-powered property report delivered to your inbox in minutes.
          </p>
          <ul style={{ listStyle: "none", paddingLeft: 0, marginBottom: 24 }}>
            <li style={{ display: "flex", alignItems: "flex-start", marginBottom: 12, fontSize: '0.88rem', color: '#374151' }}>
              <FaCheckCircle {...iconProps} />
              <span>Estimated value based on comparable sales</span>
            </li>
            <li style={{ display: "flex", alignItems: "flex-start", marginBottom: 12, fontSize: '0.88rem', color: '#374151' }}>
              <FaCheckCircle {...iconProps} />
              <span>Rental yield and suburb price trends</span>
            </li>
            <li style={{ display: "flex", alignItems: "flex-start", fontSize: '0.88rem', color: '#374151' }}>
              <FaCheckCircle {...iconProps} />
              <span>AI market summary and days-on-market</span>
            </li>
          </ul>
          <form onSubmit={handleSubmit} autoComplete="on">
            <div style={{ marginBottom: 10 }}>
              <input
                ref={emailRef}
                type="email"
                placeholder="Your email address"
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="email-input-same-height"
                style={{
                  fontSize: '0.88rem',
                  lineHeight: "24px",
                  height: 48,
                  minHeight: 48,
                  boxSizing: "border-box",
                  width: "100%",
                  padding: "12px 14px",
                  border: "1px solid #e5e7eb",
                  borderRadius: 12,
                  outline: "none",
                  background: "#fff",
                  transition: "border-color 0.2s",
                }}
                autoFocus
                inputMode="email"
                autoCapitalize="none"
                autoCorrect="off"
                spellCheck={false}
              />
            </div>
            <div style={{ marginBottom: 10 }}>
              <AsyncSelect
                cacheOptions
                defaultOptions
                loadOptions={loadProperties}
                onChange={setSelectedProperty}
                value={selectedProperty}
                noOptionsMessage={() => null}
                placeholder="Search your property address"
                openMenuOnClick={false}
                styles={selectStyles}
                {...(!isMobile ? { menuPortalTarget: document.body, menuPosition: "fixed" } : {})}
                menuPlacement="auto"
              />
            </div>
            {error && <Alert variant="danger" className="mt-2 mb-1" style={{ fontSize: '0.85rem', borderRadius: 12, border: 'none' }}>{error}</Alert>}
            <button
              type="submit"
              className="w-100 mt-2 email-btn-same-height"
              style={{
                background: accentColor,
                border: isLight ? '2px solid #222' : 'none',
                color: buttonTextColor,
                fontWeight: 600,
                fontSize: '0.95rem',
                height: 48,
                minHeight: 48,
                borderRadius: 12,
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                marginTop: 10,
                padding: 0,
                boxShadow: `0 4px 16px ${accentColor}30`,
                transition: 'opacity 0.15s',
              }}
              onMouseEnter={e => e.currentTarget.style.opacity = '0.9'}
              onMouseLeave={e => e.currentTarget.style.opacity = '1'}
            >
              Send me my free report
            </button>
          </form>
          <p style={{ fontSize: '0.72rem', color: "#9ca3af", marginTop: 14, textAlign: "center" }}>
            Free forever. No spam. Unsubscribe anytime.
          </p>
        </>
      )}
    </div>
  );

  // MOBILE: custom fullscreen overlay, vertically centered
  if (isMobile && show) {
    return (
      <>
        <div className="custom-mobile-modal-overlay">
          <div className="custom-mobile-modal-content">
            {content}
          </div>
        </div>
        <style>{`
          .custom-mobile-modal-overlay {
            position: fixed;
            inset: 0;
            z-index: 99999;
            width: 100vw;
            height: 100dvh;
            height: 100vh;
            background: #fff;
            overflow-y: auto;
            -webkit-overflow-scrolling: touch;
          }
          .custom-mobile-modal-content {
            width: 100%;
            min-height: 100%;
            background: #fff;
            box-sizing: border-box;
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: flex-start;
            padding-left: 2.2rem;
            padding-right: 2.2rem;
            padding-top: 4rem;
            padding-bottom: 40vh;
          }
          .modal-title-custom {
            font-size: 1.55rem !important;
            padding-bottom: 0.6rem !important;
          }
          .modal-desc-custom {
            font-size: 1.08rem !important;
            margin-bottom: 14px !important;
          }
          .custom-mobile-modal-content ul li {
            font-size: 1.08rem !important;
            margin-bottom: 12px !important;
          }
          .custom-mobile-modal-content ul li svg {
            width: 18px !important;
            height: 18px !important;
            min-width: 18px !important;
            margin-right: 8px !important;
            margin-top: 3px !important;
          }
          input[type="email"].email-input-same-height {
            font-size: 16px !important;
            line-height: 24px !important;
            height: 48px !important;
            min-height: 48px !important;
            box-sizing: border-box;
          }
          button.email-btn-same-height {
            height: 48px !important;
            min-height: 48px !important;
            box-sizing: border-box;
            font-size: 17px !important;
          }
        `}</style>
      </>
    );
  }

  // DESKTOP: use Modal, vertically centered
  return (
    <Modal
      show={show}
      onHide={onHide}
      centered
      style={{ zIndex: 9999 }}
      backdrop={true}
      keyboard={true}
      contentClassName="p-0"
      dialogClassName="almost-fullscreen-modal"
    >
      <Modal.Body
        className="modal-body-custom"
        style={{
          padding: "28px",
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "350px",
          position: "relative",
          borderRadius: 16,
        }}
      >
        {content}
      </Modal.Body>
      <style>{`
        @media (max-width: 600px) {
          .almost-fullscreen-modal {
            display: none !important;
          }
        }
        @media (min-width: 601px) {
          .modal-body-custom {
            display: flex;
            flex-direction: column;
            justify-content: center;
            align-items: center;
            min-height: 350px;
          }
          .modal-title-custom {
            font-size: 1.3rem !important;
            padding-bottom: 0.5rem !important;
          }
          .modal-desc-custom {
            font-size: 1rem !important;
            margin-bottom: 10px !important;
          }
          .email-input-same-height,
          .email-btn-same-height {
            height: 48px !important;
            min-height: 48px !important;
            max-height: 48px !important;
            box-sizing: border-box;
          }
          .email-btn-same-height {
            display: flex;
            align-items: center;
            justify-content: center;
            padding-top: 0 !important;
            padding-bottom: 0 !important;
          }
        }
      `}</style>
    </Modal>
  );
}

export default ArticleEmailModal;